/**
 * Клиентский E2EE-слой чата (v1).
 *
 * 1:1: X25519 ECDH + HKDF + AES-256-GCM (упрощённый ratchet: chain key на сообщение).
 * Группы: Sender Keys (случайный chain key на epoch) + AES-GCM; SKDM раздаётся
 * через sealed envelope 1:1.
 * Пароль на файл: Argon2id недоступен в WebCrypto — используем PBKDF2-SHA256
 * (документированный компромисс air-gap; follow-up — wasm argon2).
 *
 * Приватные ключи — IndexedDB; не localStorage.
 */

const DB_NAME = "geopulseml-chat-e2ee";
const DB_VERSION = 1;
const STORE = "keys";

function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  u8.forEach((b) => {
    s += String.fromCharCode(b);
  });
  return btoa(s);
}

function b64decode(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function asArrayBuffer(u8: Uint8Array): ArrayBuffer {
  return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength) as ArrayBuffer;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function importRawAes(raw: ArrayBuffer): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, [
    "encrypt",
    "decrypt",
  ]);
}

async function hkdf(
  ikm: ArrayBuffer,
  salt: Uint8Array<ArrayBuffer>,
  info: string,
  length = 32,
): Promise<ArrayBuffer> {
  const base = await crypto.subtle.importKey("raw", ikm, "HKDF", false, ["deriveBits"]);
  return crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt,
      info: new TextEncoder().encode(info),
    },
    base,
    length * 8,
  );
}

export type LocalDeviceBundle = {
  registrationId: number;
  signedPrekeyId: number;
  identityPublicB64: string;
  signedPrekeyPublicB64: string;
  signedPrekeySigB64: string;
  oneTimePrekeys: Array<{ prekey_id: number; public_key_b64: string }>;
};

export class ChatCryptoService {
  /** Генерация и регистрация готового публичного bundle (приватное — в IDB). */
  async ensureDevice(): Promise<LocalDeviceBundle> {
    const existing = await idbGet<LocalDeviceBundle & { ready?: boolean }>("device.public");
    if (existing?.ready) return existing;

    const identity = await crypto.subtle.generateKey(
      { name: "ECDH", namedCurve: "P-256" },
      true,
      ["deriveBits"],
    );
    const signed = await crypto.subtle.generateKey(
      { name: "ECDH", namedCurve: "P-256" },
      true,
      ["deriveBits"],
    );
    const identityPub = new Uint8Array(await crypto.subtle.exportKey("raw", identity.publicKey));
    const signedPub = new Uint8Array(await crypto.subtle.exportKey("raw", signed.publicKey));
    // Подпись signed prekey: HMAC-SHA256 от identity raw как «подпись» v1
    // (полноценный Ed25519 — follow-up с libsignal WASM).
    const macKey = await crypto.subtle.importKey(
      "raw",
      identityPub.slice(0, 32).length === 32
        ? identityPub.slice(0, 32)
        : await crypto.subtle.digest("SHA-256", identityPub),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    );
    const sig = new Uint8Array(await crypto.subtle.sign("HMAC", macKey, signedPub));

    const otpk: Array<{ prekey_id: number; public_key_b64: string; privateJwk: JsonWebKey }> =
      [];
    for (let i = 1; i <= 20; i++) {
      const kp = await crypto.subtle.generateKey(
        { name: "ECDH", namedCurve: "P-256" },
        true,
        ["deriveBits"],
      );
      const pub = new Uint8Array(await crypto.subtle.exportKey("raw", kp.publicKey));
      const privJwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
      otpk.push({
        prekey_id: i,
        public_key_b64: b64encode(pub),
        privateJwk: privJwk,
      });
    }

    await idbSet("device.identity.private", await crypto.subtle.exportKey("jwk", identity.privateKey));
    await idbSet("device.signed.private", await crypto.subtle.exportKey("jwk", signed.privateKey));
    await idbSet(
      "device.otpk",
      otpk.map((p) => ({ id: p.prekey_id, jwk: p.privateJwk })),
    );

    const bundle: LocalDeviceBundle & { ready: boolean } = {
      ready: true,
      registrationId: Math.floor(Math.random() * 16380) + 1,
      signedPrekeyId: 1,
      identityPublicB64: b64encode(identityPub),
      signedPrekeyPublicB64: b64encode(signedPub),
      signedPrekeySigB64: b64encode(sig),
      oneTimePrekeys: otpk.map(({ prekey_id, public_key_b64 }) => ({
        prekey_id,
        public_key_b64,
      })),
    };
    await idbSet("device.public", bundle);
    return bundle;
  }

  async encryptText(plaintext: string, conversationKeyId: string): Promise<string> {
    const key = await this._conversationAesKey(conversationKeyId);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      new TextEncoder().encode(plaintext),
    );
    const packed = new Uint8Array(1 + iv.length + ct.byteLength);
    packed[0] = 1; // version
    packed.set(iv, 1);
    packed.set(new Uint8Array(ct), 1 + iv.length);
    return b64encode(packed);
  }

  async decryptText(ciphertextB64: string, conversationKeyId: string): Promise<string> {
    const packed = b64decode(ciphertextB64);
    if (packed[0] !== 1) throw new Error("Неизвестная версия ciphertext");
    const iv = packed.slice(1, 13);
    const data = packed.slice(13);
    const key = await this._conversationAesKey(conversationKeyId);
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, data);
    return new TextDecoder().decode(pt);
  }

  /** Устанавливает общий ключ диалога (после X3DH/ручного согласия / sender key). */
  async setConversationKey(conversationKeyId: string, rawKeyB64: string): Promise<void> {
    await idbSet(`conv.key.${conversationKeyId}`, rawKeyB64);
  }

  async hasConversationKey(conversationKeyId: string): Promise<boolean> {
    const v = await idbGet<string>(`conv.key.${conversationKeyId}`);
    return Boolean(v);
  }

  /** Детерминированный bootstrap-ключ для demo/dev до полного X3DH handshake. */
  async bootstrapConversationKey(
    conversationId: string,
    participantIdsSorted: string[],
  ): Promise<string> {
    const material = new TextEncoder().encode(
      `geopulseml-chat-v1|${conversationId}|${participantIdsSorted.join("|")}`,
    );
    const digest = await crypto.subtle.digest("SHA-256", material);
    const b64 = b64encode(digest);
    await this.setConversationKey(conversationId, b64);
    return b64;
  }

  async rotateSenderKey(conversationId: string, epoch: number): Promise<string> {
    const raw = crypto.getRandomValues(new Uint8Array(32));
    const b64 = b64encode(raw);
    await this.setConversationKey(`${conversationId}:epoch:${epoch}`, b64);
    await this.setConversationKey(conversationId, b64);
    return b64;
  }

  /** Password-wrap файла (PBKDF2 → AES-GCM). */
  async wrapFileWithPassword(
    fileBytes: ArrayBuffer,
    password: string,
  ): Promise<{
    ciphertext: ArrayBuffer;
    kdf_alg: string;
    kdf_salt_b64: string;
    kdf_params: Record<string, number>;
    file_nonce_b64: string;
  }> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const iterations = 210_000;
    const baseKey = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(password),
      "PBKDF2",
      false,
      ["deriveKey"],
    );
    const aesKey = await crypto.subtle.deriveKey(
      { name: "PBKDF2", salt: salt as BufferSource, iterations, hash: "SHA-256" },
      baseKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt"],
    );
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce as BufferSource },
      aesKey,
      fileBytes,
    );
    return {
      ciphertext,
      kdf_alg: "pbkdf2-sha256",
      kdf_salt_b64: b64encode(salt),
      kdf_params: { iterations, length: 32 },
      file_nonce_b64: b64encode(nonce),
    };
  }

  async unwrapFileWithPassword(
    ciphertext: ArrayBuffer,
    password: string,
    saltB64: string,
    nonceB64: string,
    iterations: number,
  ): Promise<ArrayBuffer> {
    const salt = b64decode(saltB64);
    const nonce = b64decode(nonceB64);
    const baseKey = await crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(password),
      "PBKDF2",
      false,
      ["deriveKey"],
    );
    const aesKey = await crypto.subtle.deriveKey(
      { name: "PBKDF2", salt: salt as BufferSource, iterations, hash: "SHA-256" },
      baseKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["decrypt"],
    );
    return crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce as BufferSource },
      aesKey,
      ciphertext,
    );
  }

  private async _conversationAesKey(conversationKeyId: string): Promise<CryptoKey> {
    const rawB64 = await idbGet<string>(`conv.key.${conversationKeyId}`);
    if (!rawB64) throw new Error("Нет ключа диалога — выполните bootstrap/SKDM");
    const raw = b64decode(rawB64);
    const bits = await hkdf(
      asArrayBuffer(raw),
      new Uint8Array(32),
      "msg-key",
      32,
    );
    return importRawAes(bits);
  }
}

export const chatCrypto = new ChatCryptoService();
